import { useDropdownContext } from '@app/components/various/Dropdown/context/DropdownContext';
import { isDefined } from '@app/utils/is';
import { Key } from '@app/utils/keys';
import { Dropdown } from '@typings';
import React from 'react';

interface Props {
  items: Dropdown.MenuItem[];
  selectionMode?: 'single' | 'multiple';
  selectedKeys?: string[];
  closeMenu?: () => void;
}

export const useDropdownMenuKeyboardHandler = ({ items, selectionMode, selectedKeys, closeMenu }: Props) => {
  const { isMobile, closeDropdown } = useDropdownContext();

  const focusableItemsKeys: (string | undefined)[] = [
    ...(selectionMode === 'multiple' ? ['all'] : []),
    ...items.filter(item => !item.isDisabled).map(item => item.key),
  ];

  const getDefaultFocusedItemKey = () => {
    const [selectedKey] = selectedKeys ?? [];

    if (focusableItemsKeys.includes(selectedKey) && selectionMode === 'single') {
      return selectedKey;
    }

    return focusableItemsKeys[0];
  };

  const [focusedItemKey, setFocusedItemKey] = React.useState<string | undefined>(getDefaultFocusedItemKey);

  const handleTabKey = (event: React.KeyboardEvent) => {
    if (isMobile) {
      return;
    }

    event.stopPropagation();

    if (!event.shiftKey || !isDefined(closeMenu)) {
      closeDropdown();

      return;
    }

    event.preventDefault();
    closeMenu();
  };

  const handleArrowDownKey = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    event.preventDefault();

    const focusedItemIndex = focusableItemsKeys.indexOf(focusedItemKey);
    const nextFocusedItemIndex = (focusedItemIndex + 1) % focusableItemsKeys.length;
    const nextFocusedItemKey = focusableItemsKeys[nextFocusedItemIndex];

    setFocusedItemKey(nextFocusedItemKey);
  };

  const handleArrowUpKey = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    event.preventDefault();

    const focusedItemIndex = focusableItemsKeys.indexOf(focusedItemKey);
    const prevFocusedItemIndex = (focusedItemIndex - 1 + focusableItemsKeys.length) % focusableItemsKeys.length;
    const prevFocusedItemKey = focusableItemsKeys[prevFocusedItemIndex];

    setFocusedItemKey(prevFocusedItemKey);
  };

  const handleArrowLeftKey = (event: React.KeyboardEvent) => {
    if (isMobile) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();
    closeMenu?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const handlers: Record<string, Maybe<(event: React.KeyboardEvent) => void>> = {
      [Key.UP]: handleArrowUpKey,
      [Key.DOWN]: handleArrowDownKey,
      [Key.LEFT]: handleArrowLeftKey,
      [Key.TAB]: handleTabKey,
    };

    handlers[event.code]?.(event);
  };

  return {
    focusedItemKey,
    handleKeyDown,
  };
};
